import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FooterDisclaimer, HeaderLogo, ContactUsLink, HamburgerMenuLink } from 'organisms';
import { Headings, Paragraph, Button } from '@sentara-health/optimahealth_web';

interface MessageConfig {
  title: string;
  message: string;
  icon: 'expire' | 'success' | 'warning';
}

const ProxyExpiredForm = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const focusRef = useRef<HTMLDivElement>(null);

  const [messageKey, setMessageKey] = useState('7');
  const [messageConfig, setMessageConfig] = useState<MessageConfig>({
    title: 'profile.designatedRepresentative.linkExpired',
    message: 'profile.designatedRepresentative.linkExpiredDescription',
    icon: 'expire',
  });

  // Expiry message mappings based on status
  const expireMessages: Record<string, { title: string; message: string }> = {
    '0': {
      title: 'profile.designatedRepresentative.registered',
      message: 'profile.designatedRepresentative.registeredMessage',
    },
    '7': {
      title: 'profile.designatedRepresentative.linkExpired',
      message: 'profile.designatedRepresentative.linkExpiredDescription',
    },
    '6': {
      title: 'profile.designatedRepresentative.confirmed',
      message: 'profile.designatedRepresentative.confirmedMessage',
    },
  };

  useEffect(() => {
    // Set message configuration based on the key
    const expireConfig = expireMessages[messageKey] || expireMessages['7'];
    setMessageConfig({
      title: expireConfig.title,
      message: expireConfig.message,
      icon: 'expire',
    });

    // Focus on the container after render
    setTimeout(() => {
      focusRef.current?.focus();
    }, 500);
  }, [messageKey]);

  const handleGoHome = () => {
    navigate('/');
  };

  const getIconComponent = () => {
    switch (messageConfig.icon) {
      case 'success':
        return (
          <i
            className="fa fa-check-circle text-success"
            style={{ fontSize: '4rem' }}
            aria-hidden="true"
          ></i>
        );
      case 'warning':
        return (
          <i
            className="fa fa-exclamation-circle text-warning"
            style={{ fontSize: '4rem' }}
            aria-hidden="true"
          ></i>
        );
      case 'expire':
      default:
        return (
          <i
            className="fa fa-exclamation-triangle text-warning"
            style={{ fontSize: '4rem' }}
            aria-hidden="true"
          ></i>
        );
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* Header section */}
      <header className='border-bottom'>
        {/* Container for the header */}
        <div className="container-fluid">
          {/* Navigation bar */}
          <nav className="header container-lg d-flex align-items-center justify-content-between">
            <HeaderLogo />
            <div className="d-flex flex-no-wrap align-items-center">
              {/* Contact us link, visible on extra-large and large screens */}
              <div className="d-none d-xl-block d-lg-block d-md-none">
                <ContactUsLink showIcon />
              </div>
              {/* Hamburger menu link, visible on large, medium, and small screens */}
              <div className="d-lg-none d-md-block d-sm-block">
                <HamburgerMenuLink showLogout />
              </div>
            </div>
          </nav>
        </div>
      </header>
      <div className="container-fluid" aria-live="polite">
        <div
          id="main-content"
          className='container-lg px-main'
        >
          <div data-testid="CSRProxy">
            <div>
              <div
                className="container-fluid mb-6 optima-health--proxy"
                id="proxyMainFrame"
              >
                <div className="row justify-content-center">
                  <div className="col-lg-6 col-md-8 col-sm-12 col-12">
                      <div className="text-center py-5" ref={focusRef} tabIndex={-1}>
                      {/*this icon needs to be replced with icons from UI library */}
                        <div>{getIconComponent()}</div> 
                        <Headings level={1} text={t(messageConfig.title)} variant="semiBold"/>

                        <Paragraph
                          text={t(messageConfig.message)}
                          variant="regular"
                        />
                      </div>
                 
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Footer section */}
      <footer className="container-lg mt-auto">
        {/* Component to display the footer disclaimer */}
        <FooterDisclaimer />
      </footer>
    </div>
  );
};

export default ProxyExpiredForm;
